import type { ClientMatchesData, SingleMatch } from 'types/match'

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { useStore } from 'store'
import { dimmBackground, formatDate } from 'utils'
import { replaceURLParams } from 'utils/urlParams'
import copy from 'copy.json'
import { PRICING_TYPE } from 'common'
import { clientPanelPaths, paths } from 'paths'

import Button from 'components/Button'
import PageContainer from 'components/PageContainer'
import Pagination from 'components/Pagination'
import Scroll from 'components/Scroll'
import Spinner from 'components/Spinner'
import Status from 'components/Status'
import StatusesFilter from 'components/StatusesFilter'
import Table from 'components/Table'
import { ArrowLeftIcon } from 'icons'

const texts_form = copy.common.form

// This is needed to clear location.state after refresh
window.history.replaceState(null, '')

const matchStatus: SingleMatch['status'][] = [
  'ACTIVE',
  'RESERVED',
  'BLOCKED',
  'ACCEPTED',
  'FINALIZED',
  'DELETED'
]

export default function MatchesScreen() {
  const { id } = useParams()
  const navigate = useNavigate()

  const { api } = useStore()
  const [matches, setMatches] = useState<ClientMatchesData[] | null>(null)
  const [filterStatuses, setFilterStatuses] = useState<SingleMatch['status'][]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)

  const noResults = matches && matches.length === 0
  const tableIsVisible = matches && matches.length > 0
  const paginationIsVisible = tableIsVisible && totalPages > 1
  const spinnerIsVisible = loading && !matches

  useEffect(() => {
    if (!id) return

    const getClientMatches = async () => {
      setLoading(true)

      const response = await api.getClientMatches(id, {
        statuses: filterStatuses,
        page_number: currentPage
      })
      if (response) {
        setMatches(response.content)
        setTotalPages(response.total_pages)
      }

      setLoading(false)
    }

    getClientMatches()
  }, [id, filterStatuses]) // eslint-disable-line

  const handlePageChange = (currentStep: number) => {
    replaceURLParams(currentStep, 'page', navigate)
    setCurrentPage(currentStep - 1)
  }

  const handleGoPublic = (diffId: string) => {
    window.open(`${window.location.origin}/${paths.offered_matches}/${diffId}`, '_blank')
  }

  const handleChosenStatuses = (statuses: SingleMatch['status'][]) => {
    setFilterStatuses(statuses)
  }

  useEffect(() => {
    dimmBackground()
  }, [])

  return (
    <PageContainer>
      <Header>
        <BackArrowButton onClick={() => navigate(clientPanelPaths.demand_data)}>
          <ArrowLeftIcon />
        </BackArrowButton>

        <h1>{texts_form.matches}</h1>
      </Header>

      <StatusesFilter statuses={matchStatus} onChosenStatus={handleChosenStatuses} />

      <SortSection>
        <span>{copy.page.search.search_results}</span>
      </SortSection>

      {noResults && <h2 style={{ margin: 'auto' }}>{copy.common.no_search_results}</h2>}
      {spinnerIsVisible && <Spinner style={{ margin: 'auto' }} />}

      {tableIsVisible && (
        <Scroll>
          <Table>
            <thead>
              <tr>
                <th>{texts_form.status}</th>
                <th>{texts_form.diff_id}</th>
                <th>{texts_form.pricing_type}</th>
                <th>{texts_form.created_date}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {matches?.map(match => (
                <tr key={match.diff_id}>
                  <td>
                    <Status statusType={match.status} />
                  </td>
                  <td>
                    <strong>{match.diff_id}</strong>
                  </td>
                  <td
                    style={{
                      color: match.pricing_type === 'INDIVIDUAL' ? 'var(--green)' : 'inherit'
                    }}
                  >
                    {PRICING_TYPE[match.pricing_type]}
                  </td>
                  <td>{formatDate(match.created_date)}</td>
                  <td>
                    <Button small onClick={() => handleGoPublic(match.diff_id)}>
                      {copy.common.button.look}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Scroll>
      )}

      {paginationIsVisible && (
        <StyledPagination
          numberOfSteps={totalPages}
          currentStep={currentPage + 1}
          onChange={handlePageChange}
        />
      )}
    </PageContainer>
  )
}

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;

  > h1 {
    font: var(--600-24);
  }
`

const SortSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  > span {
    font: var(--600-16);
  }

  > label {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`

const StyledPagination = styled(Pagination)`
  align-self: center;
`

const BackArrowButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
`
